import { useTranslation } from 'contexts/Localization'
import useTheme from 'hooks/useTheme'
import React from 'react'
import { Flex, Text, ArrowForwardIcon, Skeleton, InfoIcon, useTooltip } from 'vvs-uikit'
import { TokenPairImage } from 'components/TokenImage'
import { TokenPairText, TokenWrapper } from 'views/BoostCalculator/styles'
import { NftBoostedAPRSectionProps } from 'views/Farms/interfaces'
import { isNilOrUndef } from 'utils/isNilOrUndef'

const NftBoostedAPRSection: React.FC<NftBoostedAPRSectionProps> = ({ boostedAPRDetails, aprTitle, stakedDesc }) => {
  const { theme } = useTheme()
  const { t } = useTranslation()

  const {
    primaryToken,
    secondaryToken,
    originalPercentage,
    boostedPercentage,
    originStakedMinerMolesNumber,
    targetStakeMinerMolesNumber,
    originTotalBoostFactor,
    boostedTotalBoostFactor,
    pairLabel,
  } = boostedAPRDetails

  const trendingColorOfPercentage = originalPercentage < boostedPercentage ? theme.colors.success : theme.colors.failure

  const trendingColorOfStakedMinerMoles =
    originStakedMinerMolesNumber < targetStakeMinerMolesNumber ? theme.colors.success : theme.colors.failure

  const trendingColorOfBoostFactor =
    boostedTotalBoostFactor &&
    (originTotalBoostFactor < boostedTotalBoostFactor ? theme.colors.success : theme.colors.failure)

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    t(
      'Your APR% remain the same because you do not have any staked liquidity in the selected farm.  You can continue applying the boost and stake liquidity in this farm later to enjoy the boost APR%',
    ),
    { placement: 'top-end', tooltipOffset: [20, 10] },
  )

  const isNumberNil = (value) => isNilOrUndef(value) || Number.isNaN(value)

  return (
    <>
      <Flex padding="0 24px" flexDirection="column" gap="12px">
        <Text fontSize="13px" lineHeight="18px" color={theme.colors.text}>
          {aprTitle || t('BOOSTED APR (ON TOP OF CURRENT xVVS VAULT BOOST)')}
        </Text>
      </Flex>
      <Flex alignItems="center" justifyContent="space-between" padding="0 24px">
        <Flex alignItems="center">
          <TokenWrapper>
            <TokenPairImage
              primaryToken={primaryToken}
              secondaryToken={secondaryToken}
              variant="inverted"
              width={40}
              height={40}
            />
          </TokenWrapper>
          <TokenPairText>{pairLabel}</TokenPairText>
        </Flex>
        <Flex alignItems="center" gap="5px">
          {isNumberNil(originalPercentage) || originalPercentage === 0 ? (
            <Skeleton width="46px" />
          ) : (
            <Text fontSize="16px" fontWeight="600" lineHeight="24px">
              {originalPercentage}%
            </Text>
          )}

          {!isNumberNil(originalPercentage) && originalPercentage !== boostedPercentage && (
            <>
              {isNumberNil(boostedPercentage) || boostedPercentage === 0 ? (
                <Skeleton width="46px" />
              ) : (
                <>
                  <ArrowForwardIcon width="24px" height="24px" color={trendingColorOfPercentage} />

                  <Text fontSize="16px" fontWeight="600" lineHeight="24px" color={trendingColorOfPercentage}>
                    {boostedPercentage}%
                  </Text>
                </>
              )}
            </>
          )}
          {originalPercentage === boostedPercentage && (
            <Flex ref={targetRef}>
              <InfoIcon color="primary" />
              {tooltipVisible && tooltip}
            </Flex>
          )}
        </Flex>
      </Flex>
      <Flex flexDirection="column" padding="0 24px" gap="6px">
        <Flex alignItems="center" justifyContent="space-between">
          <Text fontSize="13px" lineHeight="18px" color={theme.colors.textSubtle}>
            {stakedDesc || t('Miner Moles Staked')}
          </Text>

          <Flex alignItems="center" gap="5px">
            {isNumberNil(originStakedMinerMolesNumber) ? (
              <Skeleton width="46px" />
            ) : (
              <Text fontSize="13px" lineHeight="18px" color={theme.colors.text}>
                {originStakedMinerMolesNumber}
              </Text>
            )}

            {targetStakeMinerMolesNumber !== originStakedMinerMolesNumber && (
              <>
                {isNumberNil(targetStakeMinerMolesNumber) ? (
                  <Skeleton width="46px" />
                ) : (
                  <>
                    <ArrowForwardIcon width="18px" height="18px" color={trendingColorOfStakedMinerMoles} />

                    <Text fontSize="13px" lineHeight="18px" color={trendingColorOfStakedMinerMoles}>
                      {targetStakeMinerMolesNumber}
                    </Text>
                  </>
                )}
              </>
            )}
          </Flex>
        </Flex>
        <Flex alignItems="center" justifyContent="space-between">
          <Text fontSize="13px" lineHeight="18px" color={theme.colors.textSubtle} textTransform="uppercase">
            {t('total boost factor')}
          </Text>

          <Flex alignItems="center" gap="5px">
            {isNumberNil(originTotalBoostFactor) ? (
              <Skeleton width="46px" />
            ) : (
              <Text fontSize="13px" lineHeight="18px" color={theme.colors.text}>
                {originTotalBoostFactor}x
              </Text>
            )}
            {boostedTotalBoostFactor && boostedTotalBoostFactor !== originTotalBoostFactor && (
              <>
                {isNumberNil(boostedTotalBoostFactor) ? (
                  <Skeleton width="46px" />
                ) : (
                  <>
                    <ArrowForwardIcon width="18px" height="18px" color={trendingColorOfBoostFactor} />

                    <Text fontSize="13px" lineHeight="18px" color={trendingColorOfBoostFactor}>
                      {boostedTotalBoostFactor}x
                    </Text>
                  </>
                )}
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}

export default NftBoostedAPRSection
